import { useEffect, useState, type JSX } from 'react';
import type { EffectProps } from './types';
import { markEffectActivated, isEffectActivated } from './effectRegistry';

export function MessageShatter({ params, trigger, triggerDelayMs, onComplete, children, effectKey }: EffectProps): JSX.Element {
  const alreadyActivated = effectKey ? isEffectActivated(effectKey) : false;
  const [phase, setPhase] = useState<'idle' | 'cracking' | 'shattered' | 'done'>(alreadyActivated ? 'done' : 'idle');

  const shatterDelay = (params.shatterDelayMs as number) ?? 2000;
  const durationMs = (params.durationMs as number) ?? 800;
  const showOriginalAfter = (params.showOriginalAfter as boolean) ?? false;

  useEffect(() => {
    if (alreadyActivated) return;
    const delay = (trigger === 'after_delay' ? triggerDelayMs : 0) + shatterDelay;
    const crackTimer = setTimeout(() => setPhase('cracking'), delay);
    return () => clearTimeout(crackTimer);
  }, [trigger, triggerDelayMs, shatterDelay, alreadyActivated]);

  useEffect(() => {
    if (phase !== 'cracking') return;
    const timer = setTimeout(() => setPhase('shattered'), 400);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'shattered') return;
    const timer = setTimeout(() => {
      setPhase('done');
      if (effectKey) markEffectActivated(effectKey);
      onComplete?.();
    }, durationMs);
    return () => clearTimeout(timer);
  }, [phase, durationMs, onComplete, effectKey]);

  if (phase === 'idle') {
    return <>{children}</>;
  }

  if (phase === 'done' && showOriginalAfter) {
    return <div className="sn-shatter--restored">{children}</div>;
  }

  if (phase === 'done') {
    return <div className="sn-shatter--gone" />;
  }

  return (
    <div className={`sn-shatter sn-shatter--${phase}`}>
      {children}
    </div>
  );
}
